import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import axios from 'axios';
import { semanticColors } from '@/common/styles/colorTokens';
import { typography } from '@/common/styles/typographyTokens';
import { toastUtils } from '@/common/utils/toastUtils';
import { MdEmail, MdSend, MdEdit, MdCheck, MdWarning } from 'react-icons/md';
import ModalCenter from '@/common/components/modal/ModalCenter';
import { Input } from '@/common/components/input/Input';
import Checkbox from '@/common/components/checkbox/Checkbox';
import { LoginCompanyUserInfo } from '@/common/types/login';
import { authService } from '@/services/authService';
interface EmailVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: LoginCompanyUserInfo;
}

/**
 * 회사 이메일 인증 모달
 * 회사별 이메일 인증이 필요한 경우 표시
 */
const EmailVerificationModal: React.FC<EmailVerificationModalProps> = ({
  isOpen,
  onClose,
  user
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isWrongEmail, setIsWrongEmail] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [isUpdatingEmail, setIsUpdatingEmail] = useState(false);
  
  // 이메일 중복 체크 관련 상태
  const [isCheckingDuplicate, setIsCheckingDuplicate] = useState(false);
  const [isDuplicate, setIsDuplicate] = useState<boolean | null>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * 이메일 중복 체크 API 직접 호출
   */
  const checkEmailDuplicate = async (email: string) => {
    try {
      setIsCheckingDuplicate(true);
      const mySpaceApiUrl = import.meta.env.VITE_API_URL_MY_SPACE || 'http://localhost:3005';
      const response = await axios.post(
        `${mySpaceApiUrl}/auth/check-email-duplicate`,
        { email },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        }
      );
      setIsDuplicate(response.data?.isDuplicate ?? false);
    } catch (error) {
      console.error('이메일 중복 체크 실패:', error);
      setIsDuplicate(null);
    } finally {
      setIsCheckingDuplicate(false);
    }
  };

  /**
   * 이메일 입력 시 debounce로 중복 체크
   */
  useEffect(() => {
    // 이메일이 비어있거나 유효하지 않으면 체크하지 않음
    if (!newEmail.trim() || !isValidEmail(newEmail)) {
      setIsDuplicate(null);
      return;
    }

    // 기존 타이머 취소
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // 500ms 후 중복 체크 실행
    debounceRef.current = setTimeout(() => {
      checkEmailDuplicate(newEmail);
    }, 500);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [newEmail]);

  const handleResendEmail = async () => {
    setIsLoading(true);
    
    try {      
      const result = await authService.resendVerifyEmail(user.email, user.id);
      if(result.success) {
        toastUtils.success(`${user.email}로 인증 이메일이 재발송되었습니다.`);
        onClose();
      } else {
        toastUtils.error('이메일 재발송 중 오류가 발생했습니다.');
      }
    } catch (error) {
      toastUtils.error('이메일 재발송 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateEmail = async () => {
    if (!newEmail.trim()) {
      toastUtils.error('새로운 이메일을 입력해주세요.');
      return;
    }

    if (!isValidEmail(newEmail)) {
      toastUtils.error('올바른 이메일 형식이 아닙니다.');
      return;
    }

    if (isDuplicate) {
      toastUtils.error('이미 사용 중인 이메일입니다.');
      return;
    }

    if (isCheckingDuplicate) {
      toastUtils.error('이메일 중복 확인 중입니다. 잠시 후 다시 시도해주세요.');
      return;
    }

    setIsUpdatingEmail(true);
    
    try {
      const result = await authService.resendVerifyEmail(newEmail, user.id);
      if(result.success) {
        toastUtils.success(`새로운 이메일 ${newEmail}로 인증 이메일이 재발송되었습니다.`);
        onClose();
      } else {
        toastUtils.error('이메일 업데이트 중 오류가 발생했습니다.');
      }
    } catch (error) {
      toastUtils.error('이메일 업데이트 중 오류가 발생했습니다.');
    } finally {
      setIsUpdatingEmail(false);
    }
  };

  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleClose = () => {
    setIsWrongEmail(false);
    setNewEmail('');
    setIsDuplicate(null);
    setIsCheckingDuplicate(false);
    onClose();
  };

  /**
   * 이메일 입력 가능 여부 확인
   */
  const isEmailValid = newEmail.trim() && isValidEmail(newEmail) && isDuplicate === false;

  if (!isOpen) return null;

  return (
    <ModalCenter 
      isOpen={isOpen} 
      onClose={handleClose} 
      title="회사 이메일 인증" 
      footer={
        <ModalFooter>
          {isWrongEmail ? (
            <>
              <CancelButton onClick={() => setIsWrongEmail(false)}>
                취소
              </CancelButton>
              <PrimaryButton 
                onClick={handleUpdateEmail}
                disabled={isUpdatingEmail || !isEmailValid || isCheckingDuplicate}
              >
                {isUpdatingEmail ? (
                  <LoadingWrapper>
                    <LoadingSpinner />
                    업데이트 중...
                  </LoadingWrapper>
                ) : (
                  <>
                    <MdCheck size={18} />
                    이메일 변경 및 발송
                  </>
                )}
              </PrimaryButton>
            </>
          ) : (
            <>
              <SecondaryButton onClick={handleClose}>
                나중에 하기
              </SecondaryButton>
              <PrimaryButton 
                onClick={handleResendEmail}
                disabled={isLoading}
              >
                {isLoading ? (
                  <LoadingWrapper>
                    <LoadingSpinner />
                    발송 중...
                  </LoadingWrapper>
                ) : (
                  <>
                    <MdSend size={18} />
                    인증 메일 발송
                  </>
                )}
              </PrimaryButton>
            </>
          )}
        </ModalFooter>
      }
    >
      <ModalContent>
        <IconWrapper>
          <IconBackground>
            <MdEmail size={32} />
          </IconBackground>
          <IconBadge>
            <MdSend size={14} />
          </IconBadge>
        </IconWrapper>

        {user.company && (
          <CompanyBadge>
            <CompanyName>{user.company.name}</CompanyName>
          </CompanyBadge>
        )}
        
        <TitleText>이메일 인증이 필요합니다</TitleText>

        {!isWrongEmail ? (
          <>
            <EmailCard>
              <EmailCardIcon>
                <MdEmail size={20} />
              </EmailCardIcon>
              <EmailCardContent>
                <EmailLabel>인증 대상 이메일</EmailLabel>
                <EmailValue>{user.email}</EmailValue>
              </EmailCardContent>
            </EmailCard>
            
            <DescriptionText>
              위 이메일 주소로 인증 메일을 발송합니다.<br />
              메일함을 확인하여 인증을 완료해주세요.
            </DescriptionText>

            <ActionToggle onClick={() => setIsWrongEmail(true)}>
              이메일 주소가 잘못되었나요? <span>변경하기</span>
            </ActionToggle>
          </>
        ) : (
          <EmailInputSection>
            <EmailInputHeader>
              <MdEdit size={16} />
              <EmailInputLabel>새로운 이메일 주소</EmailInputLabel>
            </EmailInputHeader>
            <InputWrapper>
              <Input
                value={newEmail}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewEmail(e.target.value)}
                placeholder="example@company.com"
                autoFocus
              />
            </InputWrapper>
            
            {/* 유효성 검사 메시지들 */}
            {newEmail && !isValidEmail(newEmail) && (
              <ErrorMessage>
                <ErrorIcon>!</ErrorIcon>
                올바른 이메일 형식이 아닙니다.
              </ErrorMessage>
            )}
            {newEmail && isValidEmail(newEmail) && isCheckingDuplicate && (
              <CheckingMessage>
                <CheckingSpinner />
                중복 확인 중...
              </CheckingMessage>
            )}
            {newEmail && isValidEmail(newEmail) && !isCheckingDuplicate && isDuplicate === true && (
              <ErrorMessage>
                <MdWarning size={14} />
                이미 사용 중인 이메일입니다.
              </ErrorMessage>
            )}
            {newEmail && isValidEmail(newEmail) && !isCheckingDuplicate && isDuplicate === false && (
              <SuccessMessage>
                <MdCheck size={14} />
                사용 가능한 이메일입니다.
              </SuccessMessage>
            )}

            <ActionToggle onClick={() => {
              setIsWrongEmail(false);
              setNewEmail('');
            }} style={{ marginTop: '16px', justifyContent: 'flex-start', padding: 0 }}>
              <span>취소하고 돌아가기</span>
            </ActionToggle>
          </EmailInputSection>
        )}

        <InfoBox>
          <InfoIcon>💡</InfoIcon>
          <InfoText>
            인증 메일이 도착하지 않으면 스팸함을 확인해주세요.
          </InfoText>
        </InfoBox>
      </ModalContent>
    </ModalCenter>
  );
};

// Animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

// Styled Components
const ModalContent = styled.div`
  width: 100%;
  padding: 32px 24px;
  text-align: center;
`;

const IconWrapper = styled.div`
  position: relative;
  width: 88px;
  height: 88px;
  margin: 0 auto 24px;
`;

const IconBackground = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, ${semanticColors.background.brand} 0%, #6366f1 100%);
  border-radius: 24px;
  color: white;
  box-shadow: 0 12px 24px rgba(59, 130, 246, 0.3);
  animation: ${pulse} 2s ease-in-out infinite;
  transform: rotate(-5deg);
`;

const IconBadge = styled.div`
  position: absolute;
  top: -8px;
  right: -8px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${semanticColors.background.success.primary};
  border: 4px solid #fff;
  border-radius: 50%;
  color: white;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
`;

const CompanyBadge = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 6px 16px;
  background: ${semanticColors.background.brand}10;
  border: 1px solid ${semanticColors.background.brand}30;
  border-radius: 100px;
  margin-bottom: 12px;
`;

const CompanyName = styled.span`
  ${typography.caption.caption1.medium}
  color: ${semanticColors.text.brand};
  letter-spacing: -0.01em;
`;

const TitleText = styled.h3`
  ${typography.heading.h5}
  color: ${semanticColors.text.primary};
  margin: 0 0 12px 0;
  letter-spacing: -0.02em;
`;

const EmailCard = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  background: ${semanticColors.background.tertiary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 16px;
  margin-bottom: 24px;
  text-align: left;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${semanticColors.border.brand};
    background: ${semanticColors.background.secondary};
  }
`;

const EmailCardIcon = styled.div`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border-radius: 12px;
  color: ${semanticColors.text.brand};
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
`;

const EmailCardContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const ActionToggle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 24px;
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.tertiary};
  cursor: pointer;
  transition: all 0.2s ease;

  span {
    color: ${semanticColors.text.brand};
    font-weight: 600;
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  &:hover {
    span {
      color: #4f46e5;
    }
  }
`;

const EmailLabel = styled.p`
  ${typography.caption.caption2}
  color: ${semanticColors.text.tertiary};
  margin: 0 0 4px 0;
`;

const EmailValue = styled.p`
  ${typography.paragraph.p2.medium}
  color: ${semanticColors.text.primary};
  font-weight: 600;
  margin: 0;
  word-break: break-all;
`;

const DescriptionText = styled.p`
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.secondary};
  margin: 0 0 24px 0;
  line-height: 1.6;
`;

const EmailInputSection = styled.div`
  animation: ${fadeIn} 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  text-align: left;
  padding: 20px;
  background: ${semanticColors.background.tertiary};
  border: 1px solid ${semanticColors.border.brand}40;
  border-radius: 16px;
  margin-bottom: 24px;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.02);
`;

const EmailInputHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 12px;
  color: ${semanticColors.text.secondary};
`;

const EmailInputLabel = styled.span`
  ${typography.caption.caption1.medium}
  color: ${semanticColors.text.primary};
`;

const InputWrapper = styled.div`
  margin-bottom: 10px;
`;

const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  ${typography.caption.caption1.default}
  color: ${semanticColors.text.onDanger.secondary};
  animation: ${fadeIn} 0.2s ease-out;
  margin-top: 4px;
`;

const ErrorIcon = styled.span`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${semanticColors.background.danger.primary};
  border-radius: 50%;
  color: white;
  font-size: 10px;
  font-weight: 700;
`;

const SuccessMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  ${typography.caption.caption1.default}
  color: ${semanticColors.text.onSuccess.primary};
  animation: ${fadeIn} 0.2s ease-out;
  margin-top: 4px;
`;

const CheckingMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  ${typography.caption.caption1.default}
  color: ${semanticColors.text.tertiary};
  animation: ${fadeIn} 0.2s ease-out;
  margin-top: 4px;
`;

const CheckingSpinner = styled.div`
  width: 14px;
  height: 14px;
  border: 2px solid ${semanticColors.border.primary};
  border-top-color: ${semanticColors.background.brand};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const InfoBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.08) 0%, rgba(245, 158, 11, 0.12) 100%);
  border: 1px solid rgba(245, 158, 11, 0.2);
  border-radius: 14px;
  text-align: left;
`;

const InfoIcon = styled.span`
  font-size: 18px;
  flex-shrink: 0;
  line-height: 1;
`;

const InfoText = styled.p`
  ${typography.caption.caption1.default}
  color: #b45309;
  margin: 0;
  line-height: 1.5;
`;

const ModalFooter = styled.div`
  display: flex;
  gap: 12px;
`;

const SecondaryButton = styled.button`
  ${typography.paragraph.p2.medium}
  flex: 1;
  padding: 16px 24px;
  background: #fff;
  color: ${semanticColors.text.secondary};
  border: 1.5px solid ${semanticColors.border.primary};
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${semanticColors.background.secondary};
    border-color: ${semanticColors.border.secondary};
    color: ${semanticColors.text.primary};
  }
`;

const CancelButton = styled(SecondaryButton)``;

const PrimaryButton = styled.button`
  ${typography.paragraph.p2.medium}
  flex: 1.5;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 16px 24px;
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
  color: white;
  border: none;
  border-radius: 14px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  box-shadow: 0 8px 20px rgba(79, 70, 229, 0.3);
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 12px 28px rgba(79, 70, 229, 0.4);
    filter: brightness(1.1);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  
  &:disabled {
    background: ${semanticColors.background.tertiary};
    color: ${semanticColors.text.disabled};
    box-shadow: none;
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

const LoadingWrapper = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LoadingSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

export default EmailVerificationModal;
